import React, { useState, useEffect } from "react";
import Modal from "../../../../shared/components/dashboard-layout/modalComponent";
import "./members-modal.scss";

// Utility functions
const getInitials = (name) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const MembersModal = ({ 
  isOpen, 
  onClose, 
  participants, 
  participantsLoading, 
  onAddMember,
  onRemoveMember,
  onRefreshMembers
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    if (participants?.length > 0) {
      const filtered = participants.filter(member => 
        member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredParticipants(filtered);
    } else {
      setFilteredParticipants([]);
    }
  }, [participants, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSearchTerm("");
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSaveRemoveMember = () => {
    if (isRemoving) return;
    handleConfirmRemoveMember();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleRequestRemoveMember = (member) => {
    setMemberToRemove(member);
    setShowRemoveConfirm(true);
  };

  const handleCancelRemoveMember = () => {
    setShowRemoveConfirm(false);
    setMemberToRemove(null);
  };

  const handleConfirmRemoveMember = async () => {
    if (!onRemoveMember || !memberToRemove) return;

    try {
      setIsRemoving(true);
      await onRemoveMember({ phone: memberToRemove.phone || memberToRemove.phoneNumber });
      setShowRemoveConfirm(false);
      setMemberToRemove(null);
    } finally {
      setIsRemoving(false);
    }
  };

  const MemberAvatar = ({ member }) => {
    const hasPhoto = member.photoURL || member.profilePhoto;
    const initials = getInitials(member.name || member.displayName || 'U');
    
    return (
      <div className="member-avatar">
        {hasPhoto ? (
          <img 
            src={member.photoURL || member.profilePhoto} 
            alt={member.name || member.displayName}
            className="avatar-image"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="avatar-initials" style={{ display: hasPhoto ? 'none' : 'flex' }}>
          {initials}
        </div>
      </div>
    );
  };

  const MemberItem = ({ member, onRequestRemove }) => {
    const [showActions, setShowActions] = useState(false);
    
    return (
      <div 
        className={`member-item ${member.isAdmin ? 'admin' : ''}`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className="member-left">
          <MemberAvatar member={member} />
          <div className="member-info">
            <div className="member-name">
              {member.name || member.displayName || 'Unknown User'}
              {member.isAdmin && (
                <span className="admin-badge">Admin</span>
              )}
            </div>
            <div className="member-meta">
              <span className="member-phone">
                {member.phone || member.phoneNumber}
              </span>
              {/* {member.lastSeen && (
                <span className="member-last-seen">
                  Last seen {new Date(member.lastSeen).toLocaleDateString()}
                </span>
              )} */}
              <span className="member-last-seen">
                {member.invitee ? 'Invited' : 'Joined'}
              </span>
            </div>
          </div>
        </div>
        
        <div className={`member-actions ${showActions ? 'visible' : ''}`}>
          {/* <button 
            className="action-btn message-btn" 
            title="Send Message"
            onClick={() => console.log('Message member:', member.id)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
          </button> */}
          {member.invitee ? null :
          <button
            className="action-btn remove-btn"
            title="Remove Member"
            onClick={() => onRequestRemove && onRequestRemove(member)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>}
          {/* <button 
            className="action-btn more-btn" 
            title="More Options"
            onClick={() => console.log('More options for:', member.id)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="1"/>
              <circle cx="12" cy="5" r="1"/>
              <circle cx="12" cy="19" r="1"/>
            </svg>
          </button> */}
        </div>
      </div>
    );
  };

  const LoadingState = () => (
    <div className="loading-state">
      <div className="loading-spinner">
        <div className="spinner-ring"></div>
      </div>
      <p>Loading members...</p>
    </div>
  );

  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
      <h3>No members found</h3>
      <p>{searchTerm ? `No members match "${searchTerm}"` : 'This group has no members yet'}</p>
    </div>
  );

  return (
    <div 
      className="members-modal-overlay" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="members-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-left">
            <div className="header-title">
              <h2>Group Members</h2>
              <span className="member-count">
                {participants?.length || 0} {participants?.length === 1 ? 'member' : 'members'}
              </span>
            </div>
            <div className="header-actions">
              <button 
                className="refresh bouncy-button small" 
                onClick={onRefreshMembers}
                disabled={participantsLoading}
                title="Refresh members"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                  <polyline stroke="#000" points="23 4 23 10 17 10"/>
                  <polyline stroke="#000" points="1 20 1 14 7 14"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" style={{ stroke: "#000" }}/>
                </svg>


              </button>
              <button className="bouncy-button small" onClick={onAddMember}>
                <span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Member
                </span>
              </button>
            </div>
          </div>

          

          <button className="close-button" onClick={onClose} aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-content">
          <div className="search-section">
            <div className="search-input-container">
              <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search" 
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="members-list-container">
            {participantsLoading ? (
              <LoadingState />
            ) : filteredParticipants.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="members-list">
                {filteredParticipants.map(member => (
                  <MemberItem
                    key={member.id}
                    member={member}
                    onRequestRemove={handleRequestRemoveMember}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {showRemoveConfirm && (
          <Modal
            type={"error"}
            show={showRemoveConfirm}
            onClose={handleCancelRemoveMember}
            header={"Remove Member"}
            buttons={true}
            onSave={handleSaveRemoveMember}
            saveLabel={isRemoving ? "Removing..." : "Remove"}
            saveDisabled={isRemoving}
          >
            <div className="delete-group-modal" role="dialog" aria-modal="true">
              <p className="confirmation-message">
                Are you sure you want to remove{" "}
                <strong>
                  {memberToRemove?.name ||
                    memberToRemove?.displayName ||
                    memberToRemove?.phone ||
                    memberToRemove?.phoneNumber}
                </strong>{" "}
                from this group?
              </p>
              <i className="warning-text">
                This action cannot be undone and this member cannot be re-added.
              </i>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default MembersModal;